<!-- SlaConfig.svelte -->
<script>
	import { onMount } from "svelte";
	import { format } from "svelte-i18n";
	import { user } from "../user/user.state.svelte";
	import * as slaService from "./service.js";

	let slaConfig = $state({
		critical_days: 30,
		high_days: 60,
		medium_days: 90,
		low_days: 365,
	});

	let isLoading = $state(true);
	let isSaving = $state(false);
	let notification = $state({ type: "", message: "" });

	// Estado visual de los errores de cada input
	let formErrors = $state({
		critical: false,
		high: false,
		medium: false,
		low: false,
	});

	let canEdit = $derived(["administrator"].includes(user.data.role));

	onMount(async () => {
		try {
			slaConfig = await slaService.get();
		} catch (error) {
			showNotification("danger", $format("page.sla.messages.error_load"));
			// Valores por defecto provisorios
			slaConfig = {
				critical_days: 30,
				high_days: 60,
				medium_days: 90,
				low_days: 365,
			};
		} finally {
			isLoading = false;
		}
	});

	function checkErrors() {
		let currentErrors = {
			critical: false,
			high: false,
			medium: false,
			low: false,
		};
		let isValid = true;

		// Forzamos Number para evitar comparaciones de texto
		const critical = Number(slaConfig.critical_days);
		const high = Number(slaConfig.high_days);
		const medium = Number(slaConfig.medium_days);
		const low = Number(slaConfig.low_days);

		if (critical <= 0) {
			currentErrors.critical = true;
			isValid = false;
		}

		if (critical >= high) {
			currentErrors.critical = true;
			currentErrors.high = true;
			isValid = false;
		}

		if (high >= medium) {
			currentErrors.high = true;
			currentErrors.medium = true;
			isValid = false;
		}

		if (medium >= low) {
			currentErrors.medium = true;
			currentErrors.low = true;
			isValid = false;
		}

		formErrors = currentErrors;

		return isValid;
	}

	async function saveConfig() {
		// Validamos antes de guardar. Si falla, abortamos
		if (!checkErrors()) {
			if (Number(slaConfig.critical_days) <= 0) {
				showNotification(
					"danger",
					$format("page.sla.messages.error_critical_min"),
				);
			} else {
				showNotification(
					"danger",
					$format("page.sla.messages.error_hierarchy"),
				);
			}
			return;
		}

		isSaving = true;
		notification = { type: "", message: "" };

		try {
			await slaService.save(slaConfig);

			showNotification(
				"success",
				$format("page.sla.messages.success_update"),
			);
			formErrors = {
				critical: false,
				high: false,
				medium: false,
				low: false,
			};
		} catch (error) {
			showNotification(
				"danger",
				error.message || $format("page.sla.messages.error_connection"),
			);
		} finally {
			isSaving = false;
		}
	}

	function showNotification(type, message) {
		notification = { type, message };
		if (type === "success") {
			setTimeout(() => {
				notification = { type: "", message: "" };
			}, 4000);
		}
	}
</script>

<div class="content-wrapper">
	<section class="content-header">
		<div class="container-fluid">
			<div class="row mb-2">
				<div class="col-sm-6">
					<h1 class="mt-3 mb-3 page-title">
						<i class="fa-xs fas fa-stopwatch me-1"></i>
						{$format("page.sla.title")}
					</h1>
				</div>
			</div>
		</div>
	</section>

	<section class="content">
		<div class="container-fluid">
			<!-- Notificaciones AdminLTE 4 -->
			{#if notification.message}
				<div
					class="alert alert-{notification.type} alert-dismissible fade show"
					role="alert"
				>
					<i
						class="icon fas {notification.type === 'success'
							? 'fa-check'
							: 'fa-ban'} me-2"
					></i>
					{notification.message}
					<button
						type="button"
						class="btn-close"
						aria-label="Close"
						onclick={() =>
							(notification = { type: "", message: "" })}
					></button>
				</div>
			{/if}

			<div class="card card-primary-subtle">
				<div class="card-header d-flex">
					<h3 class="card-title" style="flex: 2;">
						{$format("page.sla.card_title")}
					</h3>
					<a
						href="/finding?status=open&sla_days_remaining__lte=0"
						class="text-decoration-none"
					>
						<i class="fas fa-link me-1"></i> Ver hallazgos con SLA vencido
					</a>
				</div>

				{#if isLoading}
					<div class="overlay">
						<i class="fas fa-2x fa-sync-alt fa-spin"></i>
					</div>
				{/if}

				<form
					onsubmit={(e) => {
						e.preventDefault();
						saveConfig();
					}}
					novalidate
				>
					<div class="card-body">
						<p class="text-muted mb-4">
							{$format("page.sla.explanation")}
						</p>

						<div class="row">
							<div class="col-md-3 col-sm-6">
								<div class="form-group">
									<label
										for="criticalSla"
										class="severity-label critical"
										>{$format(
											"page.sla.labels.critical",
										)}</label
									>
									<input
										type="number"
										class="form-control {formErrors.critical
											? 'is-invalid'
											: ''}"
										id="criticalSla"
										bind:value={slaConfig.critical_days}
										oninput={checkErrors}
										min="1"
										disabled={!canEdit || isLoading}
									/>
								</div>
							</div>
							<div class="col-md-3 col-sm-6">
								<div class="form-group">
									<label
										for="highSla"
										class="severity-label high"
										>{$format(
											"page.sla.labels.high",
										)}</label
									>
									<input
										type="number"
										class="form-control {formErrors.high
											? 'is-invalid'
											: ''}"
										id="highSla"
										bind:value={slaConfig.high_days}
										oninput={checkErrors}
										min="2"
										disabled={!canEdit || isLoading}
									/>
								</div>
							</div>
							<div class="col-md-3 col-sm-6">
								<div class="form-group">
									<label
										for="mediumSla"
										class="severity-label medium"
										>{$format(
											"page.sla.labels.medium",
										)}</label
									>
									<input
										type="number"
										class="form-control {formErrors.medium
											? 'is-invalid'
											: ''}"
										id="mediumSla"
										bind:value={slaConfig.medium_days}
										oninput={checkErrors}
										min="3"
										disabled={!canEdit || isLoading}
									/>
								</div>
							</div>
							<div class="col-md-3 col-sm-6">
								<div class="form-group">
									<label
										for="lowSla"
										class="severity-label low"
										>{$format("page.sla.labels.low")}</label
									>
									<input
										type="number"
										class="form-control {formErrors.low
											? 'is-invalid'
											: ''}"
										id="lowSla"
										bind:value={slaConfig.low_days}
										oninput={checkErrors}
										min="4"
										disabled={!canEdit || isLoading}
									/>
								</div>
							</div>
						</div>
					</div>

					{#if canEdit}
						<div class="card-footer">
							<button
								type="submit"
								class="btn btn-primary"
								disabled={isSaving || isLoading}
							>
								{#if isSaving}
									<i class="fas fa-spinner fa-spin me-1"></i>
									{$format("page.sla.buttons.saving")}
								{:else}
									{$format("page.sla.buttons.save_config")}
								{/if}
							</button>
						</div>
					{/if}
				</form>
			</div>
			{#if !isLoading && !canEdit}
				<div class="callout callout-info mt-3">
					<h5>
						<i class="fas fa-info me-2"></i>
						{$format("page.sla.read_only.title")}
					</h5>
					<p>{$format("page.sla.read_only.text")}</p>
				</div>
			{/if}
		</div>
	</section>
</div>

<style>
	.severity-label {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.severity-label::before {
		content: "";
		display: inline-block;
		width: 12px;
		height: 12px;
		border-radius: 50%;
	}

	/* Colores provisorios (a reemplazar por los finales) */
	.severity-label.critical::before {
		background-color: rgba(144, 70, 132, 0.9);
	}
	.severity-label.high::before {
		background-color: rgba(160, 33, 40, 0.9);
	}
	.severity-label.medium::before {
		background-color: rgba(212, 101, 47, 0.9);
	}
	.severity-label.low::before {
		background-color: rgba(247, 186, 11, 0.9);
	}
</style>
